


import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import api from "../../api/api";
import Spinner from "../../components/ui/Spinner";
import MessageBox from "../../components/ui/MessageBox";
import Button from "../../components/ui/Button";
import PurOrderHeader from "./PurOrderHeader";
import PurOrderItems from "./PurOrderItems";
import html2canvas from "html2canvas";

// --- Print Preview Modal Component ---
const PrintPreview = ({ htmlContent, onClose, order }) => {
  const hiddenRef = React.useRef(null);

  const handlePrint = () => {
    const iframe = document.getElementById("print-iframe");
    if (iframe) {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    }
  };

  const handleWhatsAppShare = async () => {
    if (!hiddenRef.current) {
      alert("Invoice content not ready.");
      return;
    }

    const countryCode = "91"; // adjust if needed
    const mobileNumber = order?.MobileNo?.replace(/[^0-9]/g, "") || "";
    if (!mobileNumber) {
      alert("Mobile number not available for WhatsApp share.");
      return;
    }
    const fullNumber = countryCode + mobileNumber;

    try {
      const canvas = await html2canvas(hiddenRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: null,
      });

      const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/png")
      );

      if (blob) {
        await navigator.clipboard.write([
          new window.ClipboardItem({
            "image/png": blob,
          }),
        ]);
        alert("Invoice copied to clipboard! You can paste in WhatsApp.");
      }
    } catch (err) {
      console.warn("Clipboard copy failed or not supported:", err);
    }

    const message = encodeURIComponent("Here is your invoice");
    window.open(`https://wa.me/${fullNumber}?text=${message}`, "_blank");
  };

  return (
    <>
      {/* Hidden div for html2canvas capture */}
      <div
        ref={hiddenRef}
        style={{
          position: "fixed",
          top: "-9999px",
          left: "-9999px",
          width: "800px",
          backgroundColor: "white",
          padding: "10px",
        }}
        dangerouslySetInnerHTML={{ __html: htmlContent }}
      />

      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
        <div className="relative w-full max-w-sm p-4 bg-white rounded-lg shadow-2xl">
          <h3 className="mb-2 text-lg font-semibold text-center">
            Invoice Preview
          </h3>
          <div className="border border-gray-300">
            <iframe
              id="print-iframe"
              srcDoc={htmlContent}
              title="Print Preview"
              style={{ width: "100%", height: "60vh", border: "none" }}
            />
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <Button
              onClick={onClose}
              className="px-4 py-2 text-white bg-red-500 rounded-lg shadow-sm hover:bg-red-600"
            >
              Close
            </Button>
            <Button
              onClick={handlePrint}
              className="px-4 py-2 text-white bg-blue-600 rounded-lg shadow-sm hover:bg-blue-700"
            >
              Print
            </Button>
            <Button
              onClick={handleWhatsAppShare}
              className="px-4 py-2 text-white bg-green-600 rounded-lg shadow-sm hover:bg-green-700"
            >
              WhatsApp
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

const PurOrderDetailPage = ({ setView, context, refreshOrders }) => {
  const { user } = useAuth();
  const [orderDetails, setOrderDetails] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [printContent, setPrintContent] = useState("");
  const [currentOrder, setCurrentOrder] = useState(null);

  // Accordion states
  const [isOrderHeaderOpen, setIsOrderHeaderOpen] = useState(false);
  const [isOrderItemsOpen, setIsOrderItemsOpen] = useState(false);

  const fetchOrderData = useCallback(
    async (showSpinner = true) => {
      if (showSpinner) setLoading(true);
      try {
        if (!context?.id) return;
        const orderData = await api.getpurOrderForEdit(context.id);

        if (orderData.Table && orderData.Table.length > 0) {
          setOrderDetails({
            ...orderData.Table[0],
            Table2: orderData.Table2 || [],
          });
        }
        setOrderItems(orderData.Table1 || []);
      } catch (err) {
        console.error(err);
        setError("Failed to load order data.");
      } finally {
        if (showSpinner) setLoading(false);
      }
    },
    [context?.id]
  );

  useEffect(() => {
    fetchOrderData();
  }, [fetchOrderData]);

  const handlePrintInvoice = async (orderId) => {
    try {
      const resp = await api.MobGetfinalizeOrder(orderId);

      const content =
        resp?.Table?.[0]?.PrintContent ||
        resp?.Table3?.[0]?.PrintContent ||
        resp?.MobGetfinalizeOrderjs?.Table?.[0]?.PrintContent ||
        resp?.data?.MobGetfinalizeOrderjs?.Table?.[0]?.PrintContent ||
        null;

      if (content) {
        setPrintContent(content);
        setCurrentOrder({ Poid: orderId, MobileNo: orderDetails?.MobileNo });
      } else {
        alert("Print content not found. Please check API response.");
        console.error("PrintContent missing in response:", resp);
      }
    } catch (err) {
      console.error("Error fetching print content:", err);
      alert("Error fetching print content.");
    }
  };

  if (loading) return <Spinner />;
  if (error) return <MessageBox message={error} />;

  // --- Accordion reusable section ---
  const AccordionSection = ({ title, isOpen, onToggle, children }) => (
    <div className="border border-gray-200 rounded-lg overflow-hidden mb-4">
      <div
        className="flex items-center justify-between p-4 bg-gray-100 cursor-pointer transition-colors duration-200 ease-in-out hover:bg-gray-200"
        onClick={onToggle}
      >
        <h2 className="text-lg font-medium text-gray-800">{title}</h2>
        <svg
          className={`w-5 h-5 text-gray-600 transform transition-transform duration-300 ease-in-out ${
            isOpen ? "rotate-180" : "rotate-0"
          }`}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </div>

      <div
        className={`transition-all duration-500 ease-in-out overflow-hidden ${
          isOpen ? "max-h-none opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="p-4 bg-white">{children}</div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {printContent && currentOrder && (
        <PrintPreview
          htmlContent={printContent}
          onClose={() => {
            setPrintContent("");
            setCurrentOrder(null);
          }}
          order={currentOrder}
        />
      )}

      {/* Top title bar */}
      <div className="p-4 bg-white shadow">
        <h1 className="mb-4 text-xl font-bold text-center text-gray-900">
          {`Order Details #${context?.id}`}
        </h1>
      </div>

      <div className="flex justify-center mt-6">
        <div className="w-full max-w-5xl p-4 sm:p-6 bg-white shadow-lg rounded-xl">
          {success && (
            <MessageBox
              message={success}
              type="success"
              onDismiss={() => setSuccess("")}
            />
          )}

          {orderDetails && (
            <AccordionSection
              title="Order Information"
              isOpen={isOrderHeaderOpen}
              onToggle={() => setIsOrderHeaderOpen(!isOrderHeaderOpen)}
            >
              <PurOrderHeader
                orderDetails={orderDetails}
                user={user}
                setSuccess={setSuccess}
                setError={setError}
                setOrderDetails={setOrderDetails}
              />
            </AccordionSection>
          )}

          <AccordionSection
            title={`Order Items (${orderItems.length})`}
            isOpen={isOrderItemsOpen}
            onToggle={() => setIsOrderItemsOpen(!isOrderItemsOpen)}
          >
            <PurOrderItems
              orderId={context?.id}
              customerId={context?.customerId}
              orderDate={context?.orderDate}
              items={orderItems}
              user={user}
              setSuccess={setSuccess}
              setError={setError}
              refetchData={() => fetchOrderData(false)}
            />
          </AccordionSection>

          {/* Bottom Actions */}
          <div className="flex justify-end gap-3 pt-4 mt-6 border-t">
            <Button
              type="button"
              onClick={() => {
                refreshOrders();
                setView("list");
              }}
              className="bg-pink-500 hover:bg-pink-600 px-4 py-2 text-white text-sm rounded"
            >
              Finish Order
            </Button>
            {/* <Button
              type="button"
              onClick={() => handlePrintInvoice(context?.id)}
              className="bg-green-500 hover:bg-green-600 px-4 py-2 text-white text-sm rounded"
            >
              Invoice
            </Button> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurOrderDetailPage;
